 
import React, { useContext, useRef, useMemo, useState, useEffect } from 'react';
import { Affix, Alert, Button, Form, Layout, Input, 
    Card, Space, InputNumber, Spin, Tag, Radio, message } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { LockOutlined, QuestionCircleOutlined } from '@ant-design/icons'; 
import get from 'lodash/get';
import { useMutation, useQuery } from '@apollo/client';
import { Loadable } from '../../components/loadable/Loadable';
import { buildPageHeaderBreadcrumb } from '../../updated/utils'; 
import { LanguageSelectorSimple } from '../../components/formItems';
import { DEFAULT_LOCALE_CODE } from '../../const'; 
import RichTextEditor from '../../components/controls/RichTextEditor/RichTextEditor';
import { OBJECTIVE_CRITERIA_TYPES_QUERY } from '../../gql/queries';
import { CREATE_OBJECTIVE_MUTATION, PATCH_OBJECTIVE_MUTATION,  } from '../../gql/mutations';
import { useLocalesQuery, useObjectiveQuery } from '../../gql/hooks';
import { Routes } from '../../routes'; 
import { routeHistory } from '@nackle/biw-web-router';
import DeleteButton from '../../components/DeleteButton';
import { CompanyContext } from '../../providers/companyProvider';   
import { useCreateEditLocales } from '../../hooks';
import { useFeatureFlag, useFeatureFlags } from '../../featureFlags';
import { flags } from '../../featureFlags/flags';
import TinyMCE from '../../components/TinyMCE/TinyMCE';
import { useDeleteObjectiveMutation } from '../../gql/hooks/mutations';  
import { useSegmentation } from '../../hooks/useSegmentation';
import { CriteriaField, formatCriteriaItem, sortCriteria } from './CreateEditCriteria';
import FormItemPublished from './FormItemPublished';
import FormItemDates from './FormItemDates';
import './styles.less';
import dayjs from 'dayjs';
import { Flex } from 'antd';
import { BadgeSelectorFormItem } from '../../updated/pages/UserStatsPages/BadgeUtils';
import classNames from 'classnames';
import { Tooltip } from 'antd';

const CreateEditObjectiveView = ( props ) => {
    const { company } = useContext( CompanyContext );
    const TinyMCERef = useRef( null );    
    const showTinyMCE = useFeatureFlag( { name: flags.USE_TINY_MCE } );
    const [ formController ] = Form.useForm();
    const [ companyDefaultLocaleCode ] = useState( company?.defaultLocaleCode || DEFAULT_LOCALE_CODE );
    const [ isEditing, setIsEditing ] = useState( ( props?.route?.params?.objectiveId ) ); 
    const [ objectiveId, setObjectiveId ] = useState( ( props?.route?.params?.objectiveId ) ); 
    const [ showRepeatableHelp, setShowRepeatableHelp  ] = useState( false );
    const [ invalidLocaleError, setInvalidLocaleError ] = useState( false );
    const [ isSaving, setIsSaving ] = useState( false ); 
    // eslint-disable-next-line no-unused-vars
    const [ currentLocaleCode, setLocales, descriptionRawHTML, handleChangeLocale, setCurrentLocaleCode, 
        validateEnglishExists ] = useCreateEditLocales( formController );
    const [ backendErrors, setbackendErrors ] = useState( [] );
    const [ deleteObjective ] = useDeleteObjectiveMutation();
    // criteria are a form value now - CriteriaField owns them, this is just the read side. Memoized
    // so the empty fallback does not hand repeatableEnabled a new array on every render
    const criteriaValue = Form.useWatch( 'criteria', formController );
    const criteria = useMemo( () => criteriaValue ?? [], [ criteriaValue ] );
    const [ hasRevision, setHasRevision ] = useState( false );
    const [ hasLoaded, setHasLoaded ] = useState( false );
    
    const initialFormValues = {
        id: objectiveId,
        isRepeatable: false,
        isPublished: false,
        brandIds: [],
        segmentIds: [],
        localeIds: [], 
        productIds: [],
        learningProfileIds: [],
        companyRelationshipIds: [],
        criteria: [],
        points: 0,
        credits: 0,
        badge: null,
        maxEngagements: 2,
        weight: 0,
        _publishAt: undefined,
        _availableAt: undefined,
        _unavailableAt: undefined,
        _locale: DEFAULT_LOCALE_CODE
    };
    
    const { data: criteriaTypesData, loading: criteriaTypesLoading, 
        error: criteriaTypesError, called: criteriaTypesCalled } = 
        useQuery( OBJECTIVE_CRITERIA_TYPES_QUERY, { fetchPolicy: 'network-only' } );
    
    const [ createObjective ] = 
        useMutation( CREATE_OBJECTIVE_MUTATION, { fetchPolicy: 'network-only' } );
    
    const [ patchObjective ] = 
        useMutation( PATCH_OBJECTIVE_MUTATION, { fetchPolicy: 'network-only' } );
    
    const { loading: segmentationLoading, initializeSegmentation, saveSegmentation, 
        renderSegmentation } = useSegmentation( formController );
 
    /*************************************
     *  ANCHOR Get Criteria Types
     *************************************/
    
    const criteriaTypes = useMemo( () => {
        if ( criteriaTypesData && !criteriaTypesLoading && criteriaTypesData && 
            criteriaTypesData.objectiveCriterionTypes?.edges ) {
            return criteriaTypesData.objectiveCriterionTypes.edges.map( i => {
                return { ...i.node };
            } );
        } else {
            return [];
        }
    }, [ criteriaTypesLoading, criteriaTypesError, criteriaTypesCalled, criteriaTypesData ] );
    
    const getCriteraType = ( id ) => {
        return criteriaTypes.find( i => id === i.id );
    };
    
    const repeatableEnabled = useMemo( () => {
        if ( !criteria || criteria.length < 1 ) {
            return true;
        }

        const hasUnrepeatableCriteria = criteria.some( i => getCriteraType( i.criterionType )?.key  === 'COURSE_COMPLETED_FIXED' );

        if ( hasUnrepeatableCriteria ) {
            formController.setFieldValue( 'isRepeatable', false );
            return false;
        }

        return true;
    }, [ criteria ] );

    /*************************************
     *  ANCHOR Get Locales 
     *************************************/
    
    const { data: localesData, loading: localesLoading, error: localesError } = 
        useLocalesQuery( { fetchPolicy: 'cache-first' } );

    const defaultLocaleName = useMemo( () => {
        const companyLocaleCode = companyDefaultLocaleCode;
        const localeList = localesData?.locales?.edges || [];
        const locale = localeList.find( i => i.node.code === companyLocaleCode );
        
        if ( locale ) {
            return locale.node.labelEnUs;
        } else {
            return companyLocaleCode;
        }
    }, [ localesData, company ] );
    
    const locales = useMemo( () => {
        if ( !localesData ) { 
            return undefined; 
        }
        
        const mappedData = localesData.locales.edges.map( i => ( { 
            key: i.node.code,
            label: i.node.labelEnUs,
        } ) );

        return mappedData.sort( ( optionA, optionB ) => {
            const labelOptionA = ( optionA?.label?.toLowerCase() || '' );
            const labelOptionB = ( optionB?.label?.toLowerCase() || '' );
            return labelOptionA.localeCompare( labelOptionB );
        } );
    }, [ localesData ] );

    /*************************************
     * ANCHOR Get Objective
     *************************************/
 
    const { data: objectiveData, loading: objectiveLoading, error: objectiveError  } = 
        useObjectiveQuery( objectiveId, { fetchPolicy: 'network-only' } );
    
    const hasGetObjectiveError = useMemo( () => isEditing && objectiveError, [ objectiveError, isEditing ] );

    const objective = useMemo( () => {

        const getDateJs = ( dateString ) => {
            if ( dateString === null || !dateString ) {
                return undefined;
            }
            return dayjs( dateString );
        };
        
        if ( isEditing && objectiveId &&  objectiveData?.objective?.node ) {
            const node = objectiveData.objective.node;
            
            setHasRevision( node.revisionId !== null ); 

            // Removed locales with null values
            let filtered = node?.locales ? node.locales.filter( 
                l => l.name !== null || l.description !== null )
                : [];
                
            setLocales( filtered.map( l => ( { 
                locale: l.code, 
                name: l.name, 
                description: l.description 
            } ) ) );
            
            return {
                id: node.id,
                criteria: sortCriteria( ( node.criteria ?? [] ).map( formatCriteriaItem ) ),
                createdAt: node.createdAt,
                updatedAt: node.updatedAt,
                isPublished: node.isPublished,
                isRepeatable: node.isRepeatable,
                badge: node.badgeId ?? null,
                points: node.points ?? 0,
                credits: node.credits ?? 0,
                maxEngagements: node.maxEngagements === 1 ? 2 : node.maxEngagements,
                publishAt: node.publishAt,
                isAvailable: node.isAvailable,
                brandIds: node.brandIds || [],
                segmentIds: node.segmentIds || [],
                localeIds: node.localeIds || [],
                productIds: node.productIds || [],
                weight: node.weight || 0,
                hasRevision: node.revisionId !== null,
                learningProfileIds: node.learningProfileIds || [],
                companyRelationshipIds: node.companyRelationshipIds || [],
                _publishAt: getDateJs( node.publishAt ),
                _availableAt: getDateJs( node.availableAt ),
                _unavailableAt: getDateJs( node.unavailableAt ),
            };
        } else {
            return undefined;
        }
    }, [ isEditing, objectiveId, objectiveData ] );

    useEffect( () => {
        if ( objective  && !hasLoaded ) {
            setHasLoaded( true );

            formController.setFields( [ 
                { name: 'criteria', value: objective.criteria },
                { name: 'isPublished', value: objective.isPublished },
                { name: 'isRepeatable', value: objective.isRepeatable },
                { name: 'maxEngagements', value: objective.maxEngagements },
                { name: 'weight', value: objective.weight },
                { name: '_publishAt', value: objective._publishAt },
                { name: '_availableAt', value: objective._availableAt },
                { name: '_unavailableAt', value: objective._unavailableAt },
                { name: 'badge', value: objective.badge },
                { name: 'points', value: objective.points },
                { name: 'credits', value: objective.credits },
            ] ); 
        }
    }, [ objective, hasLoaded ] );

    /*************************************
     *  ANCHOR Segmentation
     *************************************/

    useEffect( () => {
        if ( isEditing && objective && !segmentationLoading ) {
            const segmentation = {
                node: {
                    brandIds: objective.brandIds,
                    segmentIds: objective.segmentIds,
                    localeIds: objective.localeIds, 
                    productIds: objective.productIds,
                    learningProfileIds: objective.learningProfileIds,
                    companyRelationshipIds: objective.companyRelationshipIds,
                }
            };

            initializeSegmentation( segmentation );
        }
    }, [ isEditing, objective, segmentationLoading ] );

    /*************************************
     *  ANCHOR Event Handlers 
     *************************************/
    
    const handleDeleteClick = async () => {
        setIsSaving( true );

        try {
            await deleteObjective( { variables: { input: { id: objectiveId } } } );
            setTimeout( () => {
                message.success( 'Objective deleted' );
                routeHistory.push( Routes.ObjectivesPage.buildPath() );
                setIsSaving( false );
            }, 7000 );
        } catch ( e ) { 
            console.error( e ); 
            message.error( 'Error deleting objective!' );
            setIsSaving( false );
        }
    };

    const handleRepeatableHelpClick = () => {
        setShowRepeatableHelp( !showRepeatableHelp );
    };

    /*************************************
     *  ANCHOR Loading and Error States
     *************************************/

    const isLoading = useMemo( () => localesLoading || segmentationLoading || 
        criteriaTypesLoading || objectiveLoading, 
    [ localesLoading, segmentationLoading, criteriaTypesLoading, objectiveLoading ] ); 
    
    const hasError = useMemo( () => localesError, [ localesError ] );

    const formIsPublishedAndHasRevision = useMemo( () => objective?.isPublished && hasRevision, [ objective, hasRevision ] ); 

    /*************************************
     *  ANCHOR Form Submission 
     *************************************/
    
    const handleFormSubmission = ( values ) => {
        setbackendErrors( [] );

        let formLocales = handleChangeLocale( currentLocaleCode ).map( i => ( {
            code: i.locale,
            name: i.name,
            description: i.description
        } ) ).filter( i => !( i.name === '' && i.description  === '' ) );
        
        const invalidLocales = formLocales.filter( l => 
            l.name === '' || l.description === '' || l.description === '<p><br></p>' );

        if ( invalidLocales && invalidLocales.length > 0 ) {
            const invalidMessage = invalidLocales.map( l => {
                
                let localeName = locales.find( n => n.key === l.code );
               
                if ( l.name === '' ) {
                    return <span key={ l.locale }>{ `${ localeName?.label } is required to have a title` }<br/></span>;
                }
                if ( l.description === '' ) {
                    return <span key={ l.locale }>{ `${ localeName?.label } is required to have content` }<br/></span>;
                }
            } );

            setInvalidLocaleError( invalidMessage );
            
            return;
        } else {
            setInvalidLocaleError( false );
        }

        setIsSaving( true );
 
        let publishAt = null;
        if ( values._publishAt ) {
            publishAt = dayjs( values._publishAt ).set( 'second', 0 ).format();
        } else {
            publishAt = null;
        }

        let availableAt = dayjs().utc( ).set( 'second', 0 ).format();
        if ( values._availableAt ) { 
            availableAt = dayjs( values._availableAt ).set( 'second', 0 ).format();
        } 

        let unavailableAt = null;
        if ( values._unavailableAt ) {
            unavailableAt = dayjs( values._unavailableAt ).set( 'second', 0 ).format();
        } else {
            unavailableAt = null;
        }
  
        // Create the input for the mutation
        const input = { 
            id: isEditing ? objectiveId : undefined,
            locales: formLocales,
            isRepeatable: values.isRepeatable === undefined ? false : values.isRepeatable,
            isPublished: values.isPublished === undefined ? false : values.isPublished,
            points: !values.points ? null : values.points,
            credits: !values.credits ? null : values.credits,
            badgeId: values.badge ?? null,
            maxEngagements: values.isRepeatable && repeatableEnabled ? values.maxEngagements || 2 : 1,
            weight: values.weight || 0,
            publishAt,
            availableAt,
            unavailableAt
        };

        saveObjective( input, values );
    };
    
    /*************************************
     *  ANCHOR Save Objective 
     *************************************/
    
    const saveObjective = async ( input, formValues ) => {
        let tempId;
        let success;

        try {
            const response = isEditing ?
                await patchObjective( { variables: { input } } ) :
                await createObjective( { variables: { input } } );
            
            success = !( response?.error || response?.errors && response?.errors?.length > 0 );
           
            if ( success ) {
                await Promise.all( [ saveSegmentation( formValues, response.data.result.id, 'objective' ) ] );
                
                if ( !isEditing ) {
                    tempId = response?.data?.result?.id;
                }
            } else {
                message.error( 'Error saving objective!' );
                window.scrollTo( 0, 0 );
                setIsSaving( false );
            }
        } catch ( e ) { 
            const errors = [];
            const e1 = get( e, 'graphQLErrors[0].extensions.response.body.errors[0].msg' );
            if ( e1 ) { errors.push( e1 ); }
            const e2 = get( e, 'graphQLErrors[0].extensions.response.body.error.errors[0].message' );
            if ( e2 ) { errors.push( e2 ); }
            setbackendErrors( errors );

            message.error( 'Error saving objective!' );
            window.scrollTo( 0, 0 );
            setIsSaving( false );
        }

        if ( success && isEditing ) {
            setTimeout( () => {
                message.success( 'Objective saved' );
                setIsSaving( false );
                window.history.back();
            }, 7000 );
        } 
        
        if ( success && !isEditing ) {
            setTimeout( () => {
                if ( tempId ) {
                    message.success( 'Objective saved' );
                    setObjectiveId( tempId );
                    setIsEditing( tempId );
                    setIsSaving( false );
                    const path = window.location.pathname;
                    const newPath = path.replace( '/create', `/edit/${ tempId }` );
                    window.history.replaceState( {}, path, newPath );
                }
            }, 1000 );
        }
    };

    /*************************************
     * ANCHOR Render
    *************************************/

    return (
        <Layout className="objective-edit">
            <Layout>
                <PageHeader     
                    breadcrumb={ buildPageHeaderBreadcrumb( [
                        { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() }, 
                        { breadcrumbName: 'Incentives', path: Routes.GamificationNav.buildPath() },
                        { breadcrumbName: 'Objectives', path: Routes.ObjectivesPage.buildPath() },
                        { breadcrumbName: isEditing ? 'Edit' : 'Create' }
                    ] ) } 
                />
             
                <Card 
                    className="objective-content"
                    title={ `${ isEditing ? 'Edit' : 'Create' } Objective` }
                >

                    { ( hasError || hasGetObjectiveError ) && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }
                    
                    { /* Loading Spinner */ }
                    { isLoading &&
                        <Spin size="medium"/>
                    }
            
                    { ( !isLoading && !hasGetObjectiveError ) &&
                        <Loadable loading={ isSaving } fixed={ true }>
                            <Form
                                form={ formController }
                                name="objective-form"
                                layout="vertical"
                                initialValues={ initialFormValues }
                                onFinish={ handleFormSubmission }
                                scrollToFirstError
                            >   
                                { /* Backend Errors */ }
                                { backendErrors.length > 0 &&
                                    <div 
                                        style={ { marginBottom: '20px' } }
                                    > 
                                        {   
                                            backendErrors.map( ( i, index ) => {
                                                return ( 
                                                    <Alert
                                                        key={ index } 
                                                        type="error"
                                                        message="Error"
                                                        description={ i }
                                                    />
                                                );
                                            } )
                                        }
                                    </div>
                                }
                                
                                { /* Language Chooser */ }
                                <Form.Item 
                                    label="Language"
                                    name="_locale"
                                    rules={ [
                                        {
                                            message: `You need to add a title and content for ${ defaultLocaleName }`,
                                            validateTrigger: [],
                                            validator: ( _, ) => {
                                                if ( currentLocaleCode === companyDefaultLocaleCode ) {
                                                    return Promise.resolve();
                                                }
                                                
                                                if ( validateEnglishExists() ) {
                                                    return Promise.resolve();
                                                } else {
                                                    return Promise.reject();
                                                }
                                            }
                                        }
                                    ] }
                                >
                                    <LanguageSelectorSimple
                                        mode="select-single"
                                        languageOptions={ locales }
                                        onChange={ ( code ) => handleChangeLocale( code ) }
                                    />
                                </Form.Item>

                                { invalidLocaleError && 
                                    <Alert
                                        className="locale-error"
                                        message="Required Fields"
                                        description={ invalidLocaleError }
                                        type="error"
                                    />
                                }

                                { /* Name */ }
                                <Form.Item
                                    name="name"
                                    label="Name"
                                    rules={ [
                                        {
                                            required: true,
                                            validateTrigger: [],
                                            message: 'Name is required',
                                        },
                                        {
                                            max: 50,
                                            validateTrigger: [],
                                            message: 'Name must be less than 50 characters long.',
                                        }
                                    ] }
                                >
                                    <Input/>
                                </Form.Item>

                                { /* Description */ }
                                { showTinyMCE && 
                                    <Form.Item
                                        name="description"
                                        label="Content" 
                                        rules={ [
                                            {
                                                message: 'Please provide this objective\'s content.',
                                                validateTrigger: [],
                                                validator: ( _, ) => {
                                                    if ( TinyMCERef.current.getCharCount() > 0 ) {
                                                        return Promise.resolve();
                                                    } else {
                                                        return Promise.reject();
                                                    }
                                                }
                                            }
                                        ] }    
                                    >
                                        <TinyMCE ref={ TinyMCERef }/>
                                    </Form.Item> 
                                }

                                { !showTinyMCE &&
                                    <Form.Item
                                        name="description"
                                        label="Content"                                              
                                        rules={ [
                                            {
                                                required: true,
                                                message: 'Please provide this objective\'s content.',
                                            },
                                        ] }
                                    >
                                        <RichTextEditor 
                                            controls="simple"  
                                            locale={ currentLocaleCode }
                                            rawHTML={ descriptionRawHTML }
                                        />
                                    </Form.Item>
                                }

                                { !isEditing && 
                                    <Alert
                                        message="Additional Options"
                                        description="Click save to see additional criteria options"
                                        type="warning"
                                    />
                                }

                                { isEditing && 
                                    <>
                                        { /* Criteria Card */ }
                                        <CriteriaField
                                            objectiveId={ objectiveId }
                                            criteriaTypes={ criteriaTypes }
                                            disabled={ formIsPublishedAndHasRevision }
                                            disableDelete={ hasRevision }
                                            defaultLocaleCode={ companyDefaultLocaleCode }
                                        />

                                        { /* Settings Card */ }
                                        <Card 
                                            className="card-inner"
                                            title="Settings"
                                        >
                                            <FormItemPublished />
                                        </Card>

                                        { /* Dates Card */ }
                                        <Card 
                                            className="card-inner"
                                            title="Available Dates"
                                            extra={ objective.isAvailable && <Tag color="green"><strong>Available</strong></Tag> }
                                        >
                                            <FormItemDates hasRevision={ hasRevision }/>
                                        </Card>

                                        { /* isRepeatable Card */ }
                                        <Card 
                                            className="card-inner"
                                            title="Repeatable"
                                            extra={
                                                <Button
                                                    size="small" 
                                                    shape="circle"
                                                    onClick={ handleRepeatableHelpClick }
                                                >
                                                    <QuestionCircleOutlined />
                                                </Button>
                                            }
                                        >
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={ ( prevValues, currentValues ) => 
                                                    prevValues.isPublished !== currentValues.isPublished ||
                                                    prevValues.isRepeatable !== currentValues.isRepeatable }
                                            >
                                                { ( { getFieldValue } ) => {
                                                    return getFieldValue( 'isPublished' ) && hasRevision ? 
                                                        <>
                                                            <Alert
                                                                description="This field cannot be changed while this objective is published."
                                                                type="warning"
                                                                style={ { marginBottom: '12px' } }
                                                            />

                                                            <Form.Item 
                                                                name="isRepeatable"
                                                            >
                                                                <Radio.Group disabled={ true }>
                                                                    <Radio.Button value={ false }>Not Repeatable</Radio.Button>
                                                                    <Radio.Button value={ true }>Repeatable</Radio.Button>
                                                                </Radio.Group>
                                                            </Form.Item>

                                                            <Form.Item
                                                                label='Number of Times'
                                                                name='maxEngagements'
                                                                hidden={ getFieldValue( 'isRepeatable' ) === false }
                                                            >
                                                                <InputNumber 
                                                                    min={ getFieldValue( 'isRepeatable' ) ? 2 : 1 } 
                                                                    disabled={ true }
                                                                />
                                                            </Form.Item> 
                                                        </>
                                                        : !repeatableEnabled ?
                                                        <>
                                                            <Form.Item name="isRepeatable" disabled={ true } value={ false }>
                                                                <Radio.Group disabled={ true }>
                                                                    <Radio.Button value={ false }>Not Repeatable</Radio.Button>
                                                                    <Radio.Button value={ true }>Repeatable</Radio.Button>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                        </>
                                                        :
                                                        <>
                                                            <Form.Item name="isRepeatable">
                                                                <Radio.Group>
                                                                    <Radio.Button value={ false }>Not Repeatable</Radio.Button>
                                                                    <Radio.Button value={ true }>Repeatable</Radio.Button>
                                                                </Radio.Group>
                                                            </Form.Item>

                                                            <Form.Item
                                                                label='Number of Times'
                                                                name='maxEngagements'
                                                                hidden={ getFieldValue( 'isRepeatable' ) === false }
                                                            >
                                                                <InputNumber 
                                                                    min={ getFieldValue( 'isRepeatable' ) ? 2 : 1 } 
                                                                    disabled={ getFieldValue( 'isRepeatable' ) === false }
                                                                />
                                                            </Form.Item>
                                                        </>;
                                                } }
                                            </Form.Item> 

                                            { !repeatableEnabled &&
                                                <Alert
                                                    message="Not Repeatable"
                                                    description={ <span>Objective not repeatable with following criteria:<ul><li>Complete a specific course</li></ul></span> }
                                                    type="warning" />
                                            }
                                            
                                            { showRepeatableHelp && 
                                                <Alert
                                                    message="Repeatable"
                                                    description="If selected, users can complete the objective and receive awards multiple times - the number of times can be set under Completion limits. Note: If the type of criteria selected is specific course(s) this will award users for one completion only per user (i.e. complete Course ABC). 
                                                        If not repeatable, the user can only complete the objective once."
                                                    type="warning"
                                                />
                                            }
                                        </Card>

                                        { /* Points Card */ }
                                        
                                        <RewardsFormItem disabled={ isSaving } />
                                        
                                        { /* Weight Card */ }
                                        <Card 
                                            className="card-inner"
                                            title="Weight"
                                            extra={ <Tooltip title='Final sorting property for lists. After higher importance sorting occurs, items will show form low weight to high weight.' ><QuestionCircleOutlined /></Tooltip> }
                                        >
                                            <Form.Item
                                                label='Objective Weight'
                                                name='weight'
                                            >
                                                <InputNumber />
                                            </Form.Item>
                                        </Card>

                                        { !segmentationLoading && renderSegmentation( false ) } 
                                    </>
                                }
                            </Form>
                        </Loadable>
                    }
                </Card>
            </Layout>
            
            <Layout.Sider 
                className="side-action-bar" 
                width={ 100 } 
            >   
                <Affix offsetTop={ 95 }> 
                    <Space 
                        direction="vertical" 
                        size={ 16 } 
                    >     
                        <Button
                            disabled={ isLoading || isSaving }
                            htmlType="submit" 
                            type="primary"
                            onClick={ () => {
                                formController.submit();
                            } }
                        >
                            Save
                        </Button>
                        
                        <Button
                            disabled={ isLoading || isSaving } 
                            onClick={ () => window.history.back() }
                        >
                            Cancel
                        </Button>
                        
                        { isEditing &&
                        <DeleteButton 
                            danger = { true }
                            confirmMessage= { 
                                <React.Fragment>
                                    <p>Are you <i>sure</i> you want to permanently delete this Objective?
                                    This action cannot be undone.</p>
                                </React.Fragment>
                            } 
                            disabled={ isLoading || isSaving || hasRevision } 
                            superSafe={ false }
                            onDelete={ handleDeleteClick }
                        >
                            Delete
                        </DeleteButton>
                        }
                    </Space> 
                </Affix>
            </Layout.Sider>
        </Layout>
    );
};


const RewardsFormItem = ( { disabled } ) => {
    const [ POINTS_REWARD_ENABLED, CUSTOM_REWARD_ENABLED, BADGE_REWARD_ENABLED ] = 
        useFeatureFlags( [ flags.POINTS_REWARD_ENABLED, flags.CREDITS_REWARD_ENABLED, flags.BADGES_REWARD_ENABLED ] );

    return (
        <Card 
            className="card-inner"
            title="Rewards"
            extra={ <Tooltip title='Reward for a user completing the objective. Repeatable objectives will award the user on every completion.' ><QuestionCircleOutlined /></Tooltip> }

        >
            <Flex vertical gap={ 15 }>
                <span className='canceled'>If a reward is specified that is disabled by the company then it will not be awarded on completion</span>
                <Flex gap={ 30 }>
                    <div className='reward-item'>
                        <Form.Item
                            label={ <span className={ classNames( { canceled: !POINTS_REWARD_ENABLED } ) }>Points Reward { !POINTS_REWARD_ENABLED && <LockOutlined /> }</span> }
                            name='points'
                            rules={ [
                                {
                                    validator: ( _, value ) => {
                                        if ( !POINTS_REWARD_ENABLED && value > 0 ) {
                                            return Promise.reject( 'Points can not be awarded. This reward must be removed' );
                                        }
                                        return Promise.resolve();
                                    }
                                },
                                {
                                    max: 1000,
                                    type: 'number',
                                    message: 'Points can not be greater than 1000',
                                },                  
                            ] }
                        >
                            <NumericalRewardFormInput max={ 1000 } disabled={ disabled } rewardEnabled={ POINTS_REWARD_ENABLED } />
                        </Form.Item>
                    </div>
                    <div className='reward-item'>
                        <Form.Item
                            label={ <span className={ classNames( { canceled: !CUSTOM_REWARD_ENABLED } ) }>{ 'Credits Reward' } { !CUSTOM_REWARD_ENABLED && <LockOutlined /> }</span> }
                            name='credits'
                            rules={ [
                                {
                                    validator: ( _, value ) => {
                                        if ( !CUSTOM_REWARD_ENABLED && value > 0 ) {
                                            return Promise.reject( 'Credits can not be awarded. This reward must be removed' );
                                        }
                                        return Promise.resolve();
                                    }
                                },
                                {
                                    max: 1000,
                                    type: 'number',
                                    message: 'Reward can not be greater than 1000',
                                }                                    
                            ] }
                        >
                            <NumericalRewardFormInput max={ 1000 } disabled={ disabled } rewardEnabled={ CUSTOM_REWARD_ENABLED } />
                        </Form.Item>
                    </div>
                </Flex>
                <div className='reward-item'>
                    <Form.Item
                        label={ <span className={ classNames( { canceled: !BADGE_REWARD_ENABLED } ) }>Badge Reward { !BADGE_REWARD_ENABLED && <LockOutlined /> }</span> }
                        name='badge'
                        rules={ [
                            {
                                validator: ( _, value ) => {
                                    if ( !BADGE_REWARD_ENABLED && !!value ) {
                                        return Promise.reject( 'Badges can not be awarded. This reward must be removed' );
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ] }>
                        <BadgeSelectorFormItem rewardEnabled={ BADGE_REWARD_ENABLED } publishedOnly disabled={ disabled } />
                    </Form.Item>
                </div>
                <span className='canceled'>A non-repeatable badge on a repeatable objective will only award once</span>
            </Flex>
        </Card>
    );
};

const NumericalRewardFormInput = ( { value, onChange, disabled, max, rewardEnabled = true } ) => {
    return <Space>
        <InputNumber
            disabled={ disabled || !rewardEnabled }
            min={ 0 }
            max={ max }
            value={ value }
            onChange={ onChange }
        />
        <Button
            disabled={ disabled }
            type='primary'
            onClick={ () => onChange?.( 0 ) }
        >Reset</Button>
    </Space>;
};


export default CreateEditObjectiveView;
